import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { setSessionToken } from "../lib/storage";

interface PracticeMe {
  practice_name: string;
  role: string;
  home_org_id: string;
  clients: { org_id: string; name: string }[];
}

// Bookkeeper practice client switcher. Renders nothing for anyone who is not
// in a practice (the endpoint 404s, and also 404s everywhere while
// feature_practice_access is off), so single-org users never see it.
export function PracticeSwitcher({
  activeOrgId,
  collapsed = false,
}: {
  activeOrgId?: string;
  collapsed?: boolean;
}) {
  const [me, setMe] = useState<PracticeMe | null>(null);
  const [unhandled, setUnhandled] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PracticeMe>("/api/v1/practice/me")
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    apiFetch<{ unhandled_count: number }>("/api/v1/practice/queue/count")
      .then((r) => {
        if (!cancelled) setUnhandled(r.unhandled_count);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [me]);

  if (!me || me.clients.length === 0) return null;

  async function onChange(orgId: string) {
    const res = await apiFetch<{ access_token: string }>(
      "/api/v1/practice/switch",
      {
        method: "POST",
        body: { org_id: orgId },
      },
    );
    setSessionToken(res.access_token);
    window.location.assign("/dashboard");
  }

  // Icon-rail mode: keep the practice routes reachable as compact links.
  if (collapsed) {
    const compact =
      "relative flex size-9 items-center justify-center rounded-md text-xs font-semibold text-muted-foreground hover:bg-muted";
    return (
      <div className="mb-1 flex flex-col items-center gap-1">
        <Link to="/practice" title="Practice dashboard" aria-label="Practice dashboard" className={compact}>
          PD
        </Link>
        <Link to="/practice/queue" title="Needs you" aria-label="Needs you" className={compact}>
          NY
          {unhandled > 0 && (
            <span className="absolute right-0.5 top-0.5 size-2 rounded-full bg-brand-500" />
          )}
        </Link>
        <Link to="/practice/team" title="Practice team" aria-label="Practice team" className={compact}>
          PT
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/practice"
        className="block px-2 pb-2 text-sm font-medium text-foreground hover:underline"
      >
        Practice dashboard
      </Link>
      <Link
        to="/practice/queue"
        className="block px-2 pb-2 text-sm text-foreground hover:underline"
      >
        Needs you
        {unhandled > 0 && (
          <span
            data-testid="practice-queue-badge"
            className="ml-2 rounded-full bg-foreground px-2 py-0.5 text-xs text-background"
          >
            {unhandled}
          </span>
        )}
      </Link>
      <Link
        to="/practice/team"
        className="block px-2 pb-2 text-sm text-foreground hover:underline"
      >
        Team
      </Link>
      <label className="block px-2 pb-2 text-xs text-muted-foreground">
        Client
        <select
          data-testid="practice-client-select"
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
          value={activeOrgId ?? me.home_org_id}
          onChange={(e) => void onChange(e.target.value)}
        >
          <option value={me.home_org_id}>{me.practice_name} (practice)</option>
          {me.clients.map((c) => (
            <option key={c.org_id} value={c.org_id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
